import { z } from 'zod';
import { DEFAULT_MAX_JOKER_SLOTS } from './constants.js';
export const CurrencySchema = z.enum(['SOL', 'USDC']);
export const WagerSchema = z
    .object({
    currency: CurrencySchema,
    amountLamports: z.union([z.bigint(), z.string(), z.number()]),
})
    .transform((data) => ({
    currency: data.currency,
    amountLamports: typeof data.amountLamports === 'bigint'
        ? data.amountLamports
        : typeof data.amountLamports === 'string'
            ? BigInt(data.amountLamports)
            : BigInt(Math.round(data.amountLamports)),
}))
    .refine((data) => data.amountLamports > 0n, { message: 'Wager must be positive' });
export const SerializableWagerSchema = z.object({
    currency: CurrencySchema,
    amountLamports: z.string(),
});
export const MatchStatusSchema = z.enum([
    'open',
    'awaitingSecondPlayer',
    'inProgress',
    'settling',
    'completed',
    'cancelled',
]);
export const PlayerSchema = z.object({
    publicKey: z.string().min(32),
    displayName: z.string().min(1).max(32),
    avatarSeed: z.string().min(1),
});
export const ClusterSchema = z.enum(['devnet', 'mainnet-beta']);
export const CommitRevealSchema = z.object({
    serverSeedHash: z.string().length(64),
    serverSeed: z.string().min(32).optional(),
    clientSeed: z.string().min(16).max(256),
    signature: z.string().min(64),
});
export const MatchCreateRequestSchema = z.object({
    wager: WagerSchema,
    serverSeed: z.string().min(32).optional(),
    cluster: ClusterSchema.default('devnet'),
    deckVariant: z.string().default('standard'),
    creatorWallet: z.string().min(32),
});
export const MatchJoinRequestSchema = z.object({
    matchId: z.string().uuid(),
    clientSeed: z.string().min(16).max(256),
    signature: z.string().min(64),
});
export const TurnActionSchema = z.object({
    matchId: z.string().uuid(),
    sequence: z.number().int().nonnegative(),
    payload: z.object({
        playedCardIds: z.array(z.string()),
        modifierCardIds: z.array(z.string()),
        declaredHand: z.string(),
    }),
    signature: z.string().min(64),
});
export const MatchResultSchema = z.object({
    matchId: z.string().uuid(),
    winner: z.string(),
    loser: z.string(),
    winningScore: z.number().min(0),
    hmacNonce: z.number().int().nonnegative(),
    serverSeed: z.string().min(32),
    clientSeedP1: z.string().min(16).max(256),
    clientSeedP2: z.string().min(16).max(256),
});
export const RoomMessageSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('state'), state: z.unknown() }),
    z.object({ type: z.literal('error'), message: z.string() }),
    z.object({ type: z.literal('event'), event: z.string(), data: z.record(z.any()) }),
]);
export const ApiErrorSchema = z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.any()).optional(),
});
export const FairnessEventSchema = z.object({
    type: z.literal('commitReveal'),
    serverSeedHash: z.string().length(64),
    clientSeeds: z.array(z.string()),
});
export const ReplayEntrySchema = z.object({
    sequence: z.number().int().nonnegative(),
    deck: z.array(z.string()),
    action: z.string(),
    deltaScore: z.number(),
});
export const SettlementEventSchema = z.object({
    matchId: z.string().uuid(),
    treasuryAmount: z.string(),
    winnerAmount: z.string(),
    transactionSignature: z.string(),
    currency: CurrencySchema,
});
export const MatchRoomMetadataSchema = z.object({
    matchId: z.string().uuid(),
    createdAt: z.number(),
    expiresAt: z.number(),
    wager: SerializableWagerSchema,
    status: MatchStatusSchema,
});
export const PaginationSchema = z.object({
    page: z.number().int().nonnegative().default(0),
    pageSize: z.number().int().positive().max(100).default(20),
});
export const MatchHistoryEntrySchema = z.object({
    matchId: z.string().uuid(),
    createdAt: z.string(),
    settledAt: z.string().optional(),
    result: z.enum(['win', 'loss', 'cancelled']).optional(),
    wager: SerializableWagerSchema,
    cluster: ClusterSchema,
    settlementSignature: z.string().optional(),
});
export const BalanceSchema = z.object({
    sol: z.number(),
    usdc: z.number(),
});
export const ServerConfigSchema = z.object({
    commitment: z.string(),
    rpcEndpoint: z.string().url(),
    websocketEndpoint: z.string().url(),
    treasuryWallet: z.string(),
    programId: z.string(),
    usdcMint: z.string(),
});
export const MessageSignatureSchema = z.object({
    message: z.string(),
    signature: z.string(),
    publicKey: z.string(),
});
export const RateLimitSchema = z.object({
    limit: z.number().int().positive(),
    remaining: z.number().int().nonnegative(),
    reset: z.number().int().positive(),
});
export const GameSettingsSchema = z.object({
    motionReduced: z.boolean(),
    colorBlindMode: z.boolean(),
    autoplayAnimations: z.boolean(),
});
export const ModifierCardSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    rarity: z.enum(['common', 'rare', 'legendary']),
    effect: z.object({
        type: z.string(),
        magnitude: z.number(),
    }),
});
export const DeckCardSchema = z.object({
    id: z.string(),
    rank: z.string(),
    suit: z.string(),
    baseValue: z.number(),
});
export const ComboResultSchema = z.object({
    comboId: z.string(),
    name: z.string(),
    score: z.number(),
    multiplier: z.number(),
});
export const ScoreBreakdownSchema = z.object({
    total: z.number(),
    combo: ComboResultSchema,
    modifiers: z.array(z.object({ name: z.string(), bonus: z.number() })),
});
export const TurnResolutionSchema = z.object({
    sequence: z.number(),
    player: z.string(),
    cardsPlayed: z.array(DeckCardSchema),
    modifiers: z.array(ModifierCardSchema),
    breakdown: ScoreBreakdownSchema,
});
export const MatchSnapshotSchema = z.object({
    matchId: z.string().uuid(),
    status: MatchStatusSchema,
    players: z.array(PlayerSchema),
    currentTurn: z.string().nullable(),
    targetScore: z.number(),
    lastTurn: TurnResolutionSchema.optional(),
});
export const SettlementPayloadSchema = z.object({
    matchId: z.string().uuid(),
    winner: z.string(),
    serverSeed: z.string(),
    clientSeedP1: z.string(),
    clientSeedP2: z.string(),
    serverSeedHash: z.string(),
    proof: z.string(),
});
export const HealthResponseSchema = z.object({
    status: z.literal('ok'),
    version: z.string(),
    cluster: ClusterSchema,
    commit: z.string().optional(),
});
export const PriceEntrySchema = z.object({
    symbol: z.string(),
    value: z.number(),
    updatedAt: z.string(),
});
export const PricesResponseSchema = z.object({
    prices: z.array(PriceEntrySchema),
});
export const ReplayRequestSchema = z.object({
    matchId: z.string().uuid(),
});
export const AuthenticationTicketSchema = z.object({
    wallet: z.string(),
    issuedAt: z.string(),
    expiresAt: z.string(),
    signature: z.string(),
});
export const ColyseusSeatSchema = z.object({
    sessionId: z.string(),
    wallet: z.string(),
    connected: z.boolean(),
});
export const RunDifficultySchema = z.enum(['casual', 'standard', 'highStakes']);
export const BlindTypeSchema = z.enum(['small', 'big', 'boss']);
export const JokerRaritySchema = z.enum(['common', 'uncommon', 'rare', 'legendary']);
export const JokerEffectSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('flatScore'),
        amount: z.number(),
    }),
    z.object({
        kind: z.literal('percentScore'),
        percent: z.number(),
    }),
    z.object({
        kind: z.literal('scorePerHeart'),
        percentPerHeart: z.number(),
    }),
    z.object({
        kind: z.literal('handTypeFlat'),
        hand: z.string(),
        amount: z.number(),
    }),
    z.object({
        kind: z.literal('handTypePercent'),
        hand: z.string(),
        percent: z.number(),
    }),
    z.object({
        kind: z.literal('chipsOnClear'),
        amount: z.number().int(),
    }),
    z.object({
        kind: z.literal('heartsOnClear'),
        amount: z.number().int(),
    }),
    z.object({
        kind: z.literal('chipsOnFail'),
        amount: z.number().int(),
    }),
    z.object({
        kind: z.literal('forceSuit'),
        suit: z.enum(['Sol', 'Mint', 'Burn', 'Stake']),
    }),
    z.object({
        kind: z.literal('opponentHeartDamageOnClear'),
        amount: z.number().int().positive(),
    }),
    z.object({
        kind: z.literal('heartStealOnClear'),
        amount: z.number().int().positive(),
    }),
    z.object({
        kind: z.literal('freezeOpponentOnClear'),
    }),
]);
// =====================
// Joker Evolution System
// =====================
export const EvolutionConditionSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('triggerCount'),
        description: z.string(),
        target: z.number().int().positive(),
    }),
    z.object({
        kind: z.literal('scoreThreshold'),
        description: z.string(),
        target: z.number().int().positive(),
    }),
    z.object({
        kind: z.literal('handTypeCount'),
        description: z.string(),
        handType: z.string(),
        target: z.number().int().positive(),
    }),
    z.object({
        kind: z.literal('heartsAbove'),
        description: z.string(),
        minHearts: z.number().int().positive(),
        triggerCount: z.number().int().positive(),
    }),
    z.object({
        kind: z.literal('chipsEarned'),
        description: z.string(),
        target: z.number().int().positive(),
    }),
    z.object({
        kind: z.literal('consecutiveClears'),
        description: z.string(),
        target: z.number().int().positive(),
    }),
    z.object({
        kind: z.literal('winMatch'),
        description: z.string(),
        condition: z.string().optional(), // e.g., "with 4+ hearts"
    }),
]);
export const JokerEvolutionTierSchema = z.enum(['base', 'awakened', 'ascended']);
export const JokerEvolutionSchema = z.object({
    tier: JokerEvolutionTierSchema,
    baseJokerId: z.string().optional(), // Reference to base joker (for evolved variants)
    nextEvolution: z
        .object({
        condition: EvolutionConditionSchema,
        evolvesTo: z.string(), // ID of evolved joker
    })
        .optional(),
});
export const JokerEvolutionProgressSchema = z.object({
    jokerId: z.string(),
    currentTier: JokerEvolutionTierSchema,
    progress: z.number().int().nonnegative(),
    target: z.number().int().positive(),
    conditionMet: z.boolean(),
});
export const JokerDefinitionSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    rarity: JokerRaritySchema,
    cost: z.number().int().nonnegative(),
    maxCopies: z.number().int().positive().default(1),
    effects: z.array(JokerEffectSchema),
    tags: z.array(z.string()).default([]),
    evolution: JokerEvolutionSchema.optional(),
});
export const BlindModifierSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('scoreMultiplier'),
        multiplier: z.number().positive(),
    }),
    z.object({
        kind: z.literal('suitLock'),
        suit: z.enum(['Sol', 'Mint', 'Burn', 'Stake']),
    }),
    z.object({
        kind: z.literal('discardPenalty'),
        amount: z.number().int().nonnegative(),
    }),
    z.object({
        kind: z.literal('extraDraw'),
        amount: z.number().int().nonnegative(),
    }),
    z.object({
        kind: z.literal('targetBonus'),
        bonus: z.number().int().nonnegative(),
    }),
]);
const RunAnomalyBaseSchema = z.object({
    id: z.string(),
    description: z.string(),
    activateAfter: z.number().int().nonnegative(),
    durationInBlinds: z.number().int().positive().default(1),
    remainingBlinds: z.number().int().nonnegative().optional(),
    activatedAt: z.string().optional(),
});
export const RunAnomalySchema = z.discriminatedUnion('kind', [
    RunAnomalyBaseSchema.extend({
        kind: z.literal('doubleSuit'),
        suit: z.enum(['Sol', 'Mint', 'Burn', 'Stake']),
    }),
    RunAnomalyBaseSchema.extend({
        kind: z.literal('invertedModifiers'),
    }),
    RunAnomalyBaseSchema.extend({
        kind: z.literal('suddenBoss'),
        suit: z.enum(['Sol', 'Mint', 'Burn', 'Stake']),
        baseTarget: z.number().int().nonnegative(),
        reward: z.number().int().nonnegative(),
        scoreMultiplier: z.number().positive(),
        targetBonus: z.number().int().nonnegative(),
        generatedBlindIndex: z.number().int().nonnegative().optional(),
    }),
    // Comeback anomaly: Activated when trailing by 300+ points
    RunAnomalyBaseSchema.extend({
        kind: z.literal('comebackBonus'),
        scoreMultiplier: z.number().positive().default(1.15), // +15% score bonus
    }),
]);
export const ConsumableDefinitionSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    cost: z.number().int().nonnegative(),
    effects: z.array(z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('addChips'), amount: z.number().int() }),
        z.object({ kind: z.literal('addHearts'), amount: z.number().int().nonnegative() }),
        z.object({ kind: z.literal('addDraws'), amount: z.number().int().nonnegative() }),
        z.object({ kind: z.literal('addDiscards'), amount: z.number().int().nonnegative() }),
        z.object({ kind: z.literal('scoreMultiplier'), percent: z.number() }),
        z.object({ kind: z.literal('opponentHeartDamage'), amount: z.number().int().positive() }),
    ])),
    tags: z.array(z.string()).default([]),
});
export const MatchPowerEffectSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('selfScoreBoost'),
        amount: z.number().int(),
    }),
    z.object({
        kind: z.literal('selfHeal'),
        amount: z.number().int().positive(),
    }),
    z.object({
        kind: z.literal('selfCleanse'),
    }),
    z.object({
        kind: z.literal('selfShield'),
        charges: z.number().int().positive(),
    }),
    z.object({
        kind: z.literal('opponentScorePenalty'),
        amount: z.number().int().positive(),
    }),
    z.object({
        kind: z.literal('opponentSuitLock'),
        suit: z.string().optional(),
    }),
    z.object({
        kind: z.literal('opponentHeartDamage'),
        amount: z.number().int().positive(),
    }),
    z.object({
        kind: z.literal('selfHeartSteal'),
        amount: z.number().int().positive(),
    }),
    z.object({
        kind: z.literal('opponentFreeze'),
    }),
]);
export const MatchPowerCardSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    rarity: z.enum(['common', 'uncommon', 'rare', 'legendary']),
    effects: z.array(MatchPowerEffectSchema),
});
export const MATCH_SABOTAGE_CARDS = [
    {
        id: 'venom-dart',
        name: 'Priority Fee Spike',
        description: "Congestion hits. Opponent's next hand suffers a 40 point penalty.",
        rarity: 'uncommon',
        effects: [{ kind: 'opponentScorePenalty', amount: 40 }],
    },
    {
        id: 'tidal-curse',
        name: 'Suit Lock Exploit',
        description: "Lock opponent to a random suit — like a bugged transaction they can't cancel.",
        rarity: 'rare',
        effects: [{ kind: 'opponentSuitLock' }],
    },
    {
        id: 'void-leech',
        name: 'MEV Sandwich',
        description: 'Front-run +30 points for yourself, back-run -20 from opponent.',
        rarity: 'rare',
        effects: [
            { kind: 'selfScoreBoost', amount: 30 },
            { kind: 'opponentScorePenalty', amount: 20 },
        ],
    },
    {
        id: 'purity-wave',
        name: 'QUIC Upgrade',
        description: 'Cleanse all debuffs and reclaim 25 points. Network stability restored.',
        rarity: 'uncommon',
        effects: [{ kind: 'selfCleanse' }, { kind: 'selfScoreBoost', amount: 25 }],
    },
    {
        id: 'guardian-halo',
        name: 'Firewall Shield',
        description: 'Raise a validator firewall. Blocks the next sabotage and grants 15 points.',
        rarity: 'rare',
        effects: [
            { kind: 'selfShield', charges: 1 },
            { kind: 'selfScoreBoost', amount: 15 },
        ],
    },
    {
        id: 'heart-bloom',
        name: 'Marinade Heal',
        description: 'Liquid staking yield restores 30 health. +10 point momentum.',
        rarity: 'uncommon',
        effects: [
            { kind: 'selfHeal', amount: 30 },
            { kind: 'selfScoreBoost', amount: 10 },
        ],
    },
    {
        id: 'heart-breaker',
        name: 'Wallet Drain',
        description: 'Malicious approval — deal 1 heart damage directly to your opponent.',
        rarity: 'legendary',
        effects: [{ kind: 'opponentHeartDamage', amount: 1 }],
    },
    {
        id: 'soul-siphon',
        name: 'Rug Pull',
        description: 'Yank the liquidity — steal 1 heart from your opponent.',
        rarity: 'legendary',
        effects: [{ kind: 'selfHeartSteal', amount: 1 }],
    },
    {
        id: 'void-strike',
        name: 'FTX Contagion',
        description: 'Cascading collapse — deal 1 heart damage and apply a 25 point penalty.',
        rarity: 'legendary',
        effects: [
            { kind: 'opponentHeartDamage', amount: 1 },
            { kind: 'opponentScorePenalty', amount: 25 },
        ],
    },
    {
        id: 'network-halt',
        name: '17-Hour Outage',
        description: 'September 14, 2021. Network halted. Opponent skips their next turn entirely.',
        rarity: 'legendary',
        effects: [{ kind: 'opponentFreeze' }],
    },
];
export const ShopServiceDefinitionSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    cost: z.number().int().nonnegative(),
});
export const ShopOfferSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('joker'),
        offerId: z.string(),
        joker: JokerDefinitionSchema,
    }),
    z.object({
        kind: z.literal('consumable'),
        offerId: z.string(),
        consumable: ConsumableDefinitionSchema,
    }),
    z.object({
        kind: z.literal('service'),
        offerId: z.string(),
        service: ShopServiceDefinitionSchema,
    }),
]);
export const RunShopStateSchema = z.object({
    offers: z.array(ShopOfferSchema),
    rerollCost: z.number().int().nonnegative(),
    generation: z.number().int().nonnegative(),
});
export const ShopRollLogSchema = z.object({
    nonce: z.number().int().nonnegative(),
    wallet: z.string(),
    seed: z.string(),
    generation: z.number().int().nonnegative(),
    offers: RunShopStateSchema,
});
export const PlayerRunStateSchema = z.object({
    chips: z.number().int().nonnegative(),
    hearts: z.number().int().nonnegative(),
    totalScore: z.number().int().nonnegative(),
    jokerSlots: z.number().int().nonnegative(),
    jokers: z.array(JokerDefinitionSchema),
    consumables: z.array(ConsumableDefinitionSchema),
    consumablesUsed: z.array(z.string()),
    pendingScorePercent: z.number(),
    tempoStreak: z.number().int().nonnegative(),
    phase: z.enum(['play', 'shop']),
    nextShopTurn: z.number().int().nonnegative(),
    shopGeneration: z.number().int().nonnegative(),
    shopRerolls: z.number().int().nonnegative(),
});
export const SharedRunSnapshotSchema = z.object({
    sequence: z.number().int().nonnegative(),
    wallet: z.string(),
    state: PlayerRunStateSchema,
    shopState: RunShopStateSchema.optional(),
});
export const VerifySeedResponseSchema = z.object({
    matchId: z.string().uuid(),
    shuffleProofs: z.array(z.custom()),
    shopRolls: z.array(ShopRollLogSchema).default([]),
    sharedSnapshots: z.array(SharedRunSnapshotSchema).default([]),
});
export const FairnessReplaySchema = z.object({
    matchId: z.string().uuid(),
    entries: z.array(ReplayEntrySchema),
    shopRolls: z.array(ShopRollLogSchema).default([]),
    sharedSnapshots: z.array(SharedRunSnapshotSchema).default([]),
});
export const RunBlindSchema = z.object({
    index: z.number().int().nonnegative(),
    type: BlindTypeSchema,
    targetScore: z.number().int().positive(),
    reward: z.number().int().nonnegative(),
    penaltyHearts: z.number().int().positive().default(1),
    modifiers: z.array(BlindModifierSchema),
    description: z.string(),
});
export const RunInventorySchema = z.object({
    jokers: z.array(JokerDefinitionSchema).max(DEFAULT_MAX_JOKER_SLOTS),
    consumables: z.array(ConsumableDefinitionSchema),
});
export const RunStatusSchema = z.enum(['inProgress', 'completed', 'failed']);
export const RunHandStateSchema = z.object({
    hand: z.array(DeckCardSchema),
    drawsRemaining: z.number().int().nonnegative(),
    discardsRemaining: z.number().int().nonnegative(),
});
export const RunBlindResolutionSchema = z.object({
    blindIndex: z.number().int().nonnegative(),
    success: z.boolean(),
    score: z.number().int().nonnegative(),
    chipsAwarded: z.number().int().nonnegative(),
    heartsRemaining: z.number().int().nonnegative(),
    chipsTotal: z.number().int().nonnegative(),
    timestamp: z.string(),
    breakdown: ScoreBreakdownSchema,
});
export const RunBlindSubmitSchema = z.object({
    blindIndex: z.number().int().nonnegative(),
    cardIds: z.array(z.string()).min(2).max(5),
});
export const RunSessionSchema = z.object({
    id: z.string().uuid(),
    wallet: z.string().min(32),
    cluster: ClusterSchema,
    difficulty: RunDifficultySchema,
    seed: z.string().min(16),
    hearts: z.number().int().nonnegative(),
    chips: z.number().int().nonnegative(),
    blindIndex: z.number().int().nonnegative(),
    blinds: z.array(RunBlindSchema),
    inventory: RunInventorySchema,
    shop: RunShopStateSchema,
    status: RunStatusSchema,
    shopRerolls: z.number().int().nonnegative(),
    lastResult: RunBlindResolutionSchema.optional(),
    history: z.array(RunBlindResolutionSchema),
    completedAt: z.string().optional(),
    hand: RunHandStateSchema,
    pendingScorePercent: z.number().default(0),
    consumablesUsed: z.array(z.string()).default([]),
    createdAt: z.string(),
    updatedAt: z.string(),
    anomalyQueue: z.array(RunAnomalySchema).default([]),
    activeAnomaly: RunAnomalySchema.optional(),
    anomalyHistory: z.array(RunAnomalySchema).default([]),
});
export const RunSummarySchema = z.object({
    id: z.string().uuid(),
    wallet: z.string().min(32),
    cluster: ClusterSchema,
    difficulty: RunDifficultySchema,
    status: RunStatusSchema,
    totalChips: z.number().int().nonnegative(),
    heartsRemaining: z.number().int().nonnegative(),
    blindsCleared: z.number().int().nonnegative(),
    durationMs: z.number().int().nonnegative(),
    creditsEarned: z.number().int().nonnegative(),
    jokers: z.array(JokerDefinitionSchema.pick({ id: true, name: true, rarity: true })),
    consumablesUsed: z.array(z.string()),
    createdAt: z.string(),
    completedAt: z.string().optional(),
});
export const RunHistoryResponseSchema = z.object({
    history: z.array(RunSummarySchema),
});
export const RunStartRequestSchema = z.object({
    difficulty: RunDifficultySchema.default('standard'),
    cluster: ClusterSchema.default('devnet'),
    seed: z.string().min(16).optional(),
});
export const RunActionSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('buyJoker'),
        offerId: z.string(),
    }),
    z.object({
        type: z.literal('buyConsumable'),
        offerId: z.string(),
    }),
    z.object({
        type: z.literal('buyService'),
        offerId: z.string(),
    }),
    z.object({
        type: z.literal('rerollShop'),
    }),
    z.object({
        type: z.literal('discardCards'),
        cardIds: z.array(z.string()).min(1).max(5),
    }),
    z.object({
        type: z.literal('drawCards'),
        count: z.number().int().positive().max(5),
    }),
    z.object({
        type: z.literal('commitHand'),
        cardIds: z.array(z.string()).min(2).max(5),
    }),
    z.object({
        type: z.literal('useConsumable'),
        consumableId: z.string(),
    }),
]);
export const JoinRoomResponseSchema = z.object({
    roomId: z.string(),
    sessionId: z.string(),
    metadata: MatchRoomMetadataSchema,
});
export const SettlementBalancesSchema = z.object({
    treasuryLamports: z.bigint(),
    winnerLamports: z.bigint(),
    escrowLamports: z.bigint(),
});
export const ReplayVerificationSchema = z.object({
    matchId: z.string().uuid(),
    verified: z.boolean(),
    discrepancies: z.array(z.string()),
});
export const SequenceAckSchema = z.object({
    matchId: z.string().uuid(),
    sequence: z.number().int().nonnegative(),
});
export const RateLimitConfigSchema = z.object({
    burst: z.number().int().positive(),
    refillPerSecond: z.number().positive(),
});
// =====================
// Daily Missions System
// =====================
export const DailyMissionTypeSchema = z.enum([
    'win_matches', // Win X matches
    'score_threshold', // Score 300/500/750 in a single hand
    'hand_type', // Win with specific hand type (Flush, Full House, etc)
    'win_streak', // Win X games in a row
    'max_tempo', // Reach max tempo in a match
    'play_matches', // Play X matches (participation)
    'use_sabotage', // Use X sabotage cards
    'earn_chips', // Earn X chips total
]);
export const DailyMissionSchema = z.object({
    id: z.string(),
    type: DailyMissionTypeSchema,
    description: z.string(),
    target: z.number().int().positive(),
    progress: z.number().int().nonnegative(),
    xpReward: z.number().int().positive(),
    completed: z.boolean(),
    // For hand_type missions
    handType: z.string().optional(),
});
export const LoginStreakSchema = z.object({
    currentStreak: z.number().int().nonnegative(),
    lastLoginDate: z.string(), // ISO date string (YYYY-MM-DD)
    longestStreak: z.number().int().nonnegative(),
    xpBonusPercent: z.number().int().nonnegative(), // Current XP bonus from streak
});
export const DailyMissionsStateSchema = z.object({
    missions: z.array(DailyMissionSchema),
    loginStreak: LoginStreakSchema,
    resetTime: z.string(), // ISO timestamp when missions reset
    totalXpEarned: z.number().int().nonnegative(), // Today's XP earned
});
export const PlayerProgressSchema = z.object({
    wallet: z.string(),
    xp: z.number().int().nonnegative(),
    level: z.number().int().positive(),
    elo: z.number().int(),
    totalWins: z.number().int().nonnegative(),
    totalLosses: z.number().int().nonnegative(),
    winStreak: z.number().int().nonnegative(),
    bestWinStreak: z.number().int().nonnegative(),
    loginStreak: LoginStreakSchema,
    dailyMissions: z.array(DailyMissionSchema),
    lastMissionReset: z.string(), // ISO date
    titles: z.array(z.string()), // Unlocked titles
    activeTitle: z.string().optional(),
});
// =====================
// Ranked Seasons System
// =====================
export const RankTierSchema = z.enum([
    'bronze',
    'silver',
    'gold',
    'platinum',
    'diamond',
    'champion',
]);
export const RankInfoSchema = z.object({
    tier: RankTierSchema,
    division: z.number().int().min(1).max(4), // 4 divisions per tier (IV, III, II, I)
    elo: z.number().int(),
    peakElo: z.number().int(),
    peakTier: RankTierSchema,
});
export const SeasonSchema = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    startDate: z.string(), // ISO date
    endDate: z.string(), // ISO date
    isActive: z.boolean(),
});
export const SeasonPlacementSchema = z.object({
    seasonId: z.number().int().positive(),
    matchesPlayed: z.number().int().nonnegative(),
    matchesRequired: z.number().int().positive(), // Usually 3
    wins: z.number().int().nonnegative(),
    losses: z.number().int().nonnegative(),
    isPlaced: z.boolean(),
    placedRank: RankInfoSchema.optional(),
});
export const SeasonRankingSchema = z.object({
    wallet: z.string(),
    seasonId: z.number().int().positive(),
    rank: RankInfoSchema,
    placement: SeasonPlacementSchema,
    matchesPlayed: z.number().int().nonnegative(),
    wins: z.number().int().nonnegative(),
    losses: z.number().int().nonnegative(),
    lastMatchDate: z.string().optional(), // For decay calculation
    decayWarning: z.boolean(), // True if at risk of decay
    daysUntilDecay: z.number().int().nonnegative().optional(),
});
export const SeasonRewardSchema = z.object({
    tier: RankTierSchema,
    xpReward: z.number().int().positive(),
    title: z.string().optional(),
});
// Rank tier thresholds and decay rules
export const RANK_THRESHOLDS = {
    bronze: { min: 0, max: 999, decayDays: null }, // No decay
    silver: { min: 1000, max: 1199, decayDays: 7 },
    gold: { min: 1200, max: 1399, decayDays: 5 },
    platinum: { min: 1400, max: 1599, decayDays: 5 },
    diamond: { min: 1600, max: 1799, decayDays: 3 },
    champion: { min: 1800, max: 9999, decayDays: 3 },
};
export const SEASON_REWARDS = [
    { tier: 'bronze', xpReward: 100 },
    { tier: 'silver', xpReward: 250 },
    { tier: 'gold', xpReward: 500 },
    { tier: 'platinum', xpReward: 1000 },
    { tier: 'diamond', xpReward: 2000, title: 'Diamond Player' },
    { tier: 'champion', xpReward: 5000, title: 'Season Champion' },
];
// =====================
// Tournament System
// =====================
export const TournamentFormatSchema = z.enum([
    'swiss', // Swiss-style (play all rounds, paired by record)
    'elimination', // Single elimination bracket
    'double_elim', // Double elimination bracket
]);
export const TournamentStatusSchema = z.enum([
    'upcoming', // Registration open, not started
    'in_progress', // Tournament running
    'completed', // Tournament finished
    'cancelled', // Tournament cancelled
]);
export const TournamentModeSchema = z.enum([
    'standard', // Normal rules
    'turbo', // 15-second turns
    'high_roller', // Higher wager requirement
]);
export const TournamentSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().optional(),
    format: TournamentFormatSchema,
    mode: TournamentModeSchema,
    status: TournamentStatusSchema,
    maxPlayers: z.number().int().positive(),
    minPlayers: z.number().int().positive(),
    registeredPlayers: z.number().int().nonnegative(),
    rounds: z.number().int().positive(), // Total rounds (e.g., 5 for Swiss)
    currentRound: z.number().int().nonnegative(),
    entryFee: z.number().nonnegative().optional(), // In lamports, 0 = free
    prizePool: z.number().nonnegative(), // Total XP prize
    startTime: z.string(), // ISO timestamp
    endTime: z.string().optional(),
    registrationDeadline: z.string(),
});
export const TournamentParticipantSchema = z.object({
    wallet: z.string(),
    tournamentId: z.string().uuid(),
    registeredAt: z.string(),
    wins: z.number().int().nonnegative(),
    losses: z.number().int().nonnegative(),
    draws: z.number().int().nonnegative(),
    points: z.number().int().nonnegative(), // Swiss points
    matchesPlayed: z.number().int().nonnegative(),
    rank: z.number().int().positive().optional(), // Final standing
    dropped: z.boolean(),
});
export const TournamentMatchSchema = z.object({
    id: z.string().uuid(),
    tournamentId: z.string().uuid(),
    round: z.number().int().positive(),
    player1Wallet: z.string(),
    player2Wallet: z.string().optional(), // Null = bye
    winnerWallet: z.string().optional(),
    player1Score: z.number().int().nonnegative().optional(),
    player2Score: z.number().int().nonnegative().optional(),
    matchId: z.string().uuid().optional(), // Reference to actual match
    status: z.enum(['pending', 'in_progress', 'completed', 'bye']),
});
export const TournamentRewardSchema = z.object({
    placement: z.number().int().positive(), // 1st, 2nd, 3rd, etc.
    xpReward: z.number().int().positive(),
    title: z.string().optional(),
});
// Standard tournament rewards
export const TOURNAMENT_REWARDS = [
    { placement: 1, xpReward: 500, title: 'Tournament Champion' },
    { placement: 2, xpReward: 300 },
    { placement: 3, xpReward: 200 },
    { placement: 4, xpReward: 100 },
];
//# sourceMappingURL=types.js.map